import { useState, useEffect, useCallback } from 'react';
import { type Participant, type Position, type TenantApp } from '@inloco/shared';
import {
    createRoom, // -> roomFunctions-createRoom
    joinRoom as joinRoomFirebase, // -> roomFunctions-joinRoom
    updatePosition as updatePositionFirebase, // -> roomFunctions-updatePosition
    subscribeToRoom, // -> roomFunctions-subscribeToRoom
    removeUserFromRoom as removeUser, // -> roomFunctions-removeUserFromRoom
    addBleDeviceToRoom // -> roomFunctions-addBleDeviceToRoom
} from '../../firebase/rooms';
import type { RoomSession, RoomSessionWithoutId, RoomSessionForJoining } from '../../types/room-session';
import { useAuth, useNotifications } from '../context';
import { useTranslation } from "react-i18next";

interface UseRoomReturn {
    roomSession: RoomSession | null;
    expiredAt: number;
    participants: Participant[];
    loading: boolean;
    createNewRoom: (app: TenantApp, roomSession: RoomSessionWithoutId, position: Position) => Promise<void>;
    joinExistingRoom: (app: TenantApp, roomSession: RoomSessionForJoining, position: Position) => Promise<void>;
    updatePosition: (app: TenantApp, position: Position, userId: string) => Promise<void>;
    removeUserFromRoom: (app: TenantApp, roomId: string, userId: string) => Promise<void>;
    saveBluetoothToRoom: (app: TenantApp, roomId: string, macAddress: string, name: string) => Promise<void>;
}

interface UseRoomProps {
    app: TenantApp | null;
}

export const useRoom = (props: UseRoomProps): UseRoomReturn => {

    const { app } = props;

    const { userFirebase } = useAuth();

    const { t } = useTranslation();

    const { addNotification } = useNotifications();

    const [expiredAt, setExpiredAt] = useState<number>(0)

    const [loading, setLoading] = useState<boolean>(false);

    const [participants, setParticipants] = useState<Participant[]>([]);

    const [error, setError] = useState<string | null>(null);

    const [roomSession, setRoomSession] = useState<RoomSession | null>(() => {
        try {
            const saved = localStorage.getItem('roomSession');
            if (!saved) return null;

            const rs = JSON.parse(saved) as RoomSession;

            if (rs.expiresAt && rs.offsetServer !== undefined) {
                const now = Date.now() + rs.offsetServer;
                if (now > rs.expiresAt) {
                    localStorage.removeItem('roomSession');
                    return null;  // Room expired
                }
            }

            return rs;
        } catch {
            return null;
        }
    });

    const createNewRoom = useCallback(async (
        app: TenantApp,
        roomSessionData: RoomSessionWithoutId,
        position: Position
    ): Promise<void> => {
        try {
            setLoading(true);

            // create room with Cloud Function
            const { roomId, createdAt, expiresAt } = await createRoom(
                app,
                roomSessionData,
                position
            );

            // Calculate the offset relative to the moment the response arrives at the client.
            // This is used by your SessionDialog component to run the timer correctly.
            const offset = createdAt - Date.now();

            // Enrich the sessionRoom object with the data received from the server
            const rs: RoomSession = {
                ...roomSessionData,
                roomId,
                offsetServer: offset,
                createdAt,
                expiresAt
            };

            // Update local state
            setRoomSession(rs);
            setExpiredAt(expiresAt);
            // Persistiamo in localStorage
            localStorage.setItem('roomSession', JSON.stringify(rs));
            addNotification({ message: t('success.rooms.createSuccess'), type: 'success' });

        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [addNotification, t]);

    const joinExistingRoom = useCallback(async (
        app: TenantApp,
        roomSession: RoomSessionForJoining,
        position: Position): Promise<void> => {

        try {
            setLoading(true);

            // Call the centralized logic in rooms.ts (which uses httpsCallable)
            // We pass roomId and username extracted from the initial session (e.g., from QR)
            const result = await joinRoomFirebase(
                app,
                roomSession.roomId,
                roomSession.username,
                position
            );

            // If the Cloud Function did not throw errors (room found and valid):
            if (result) {
                const { createdAt, expiresAt, serverTime } = result;

                // Calculate the offset relative to the moment the response arrives at the client.
                const offset = serverTime - Date.now();

                const enrichedSession: RoomSession = {
                    ...roomSession,
                    durationHours: (expiresAt - createdAt) / (1000 * 60 * 60), // Convert ms to hours
                    offsetServer: offset,
                    createdAt: createdAt,
                    expiresAt: expiresAt
                };

                // Update state and persistence
                setRoomSession(enrichedSession);
                setExpiredAt(expiresAt);
                localStorage.setItem('roomSession', JSON.stringify(enrichedSession));
                addNotification({ message: t('success.rooms.joinSuccess'), type: 'success' });
            }

        } catch (error: any) {
            // Here we handle errors thrown by the Cloud Function (404, 412, etc.)
            setError(error.message);

            // If the room does not exist or has expired, clean up any remnants
            localStorage.removeItem('roomSession');
        } finally {
            setLoading(false);
        }
    }, [addNotification, t]);

    const updatePosition = useCallback(async (app: TenantApp, position: Position, userId: string): Promise<void> => {
        if (roomSession && position) {
            try {
                // Updates only loc/{roomId}/{userId} (lt, lg, ac, lu)
                await updatePositionFirebase(app, roomSession.roomId, userId, position);
            } catch (error: any) {
                // SAFETY NET: Intercepts PERMISSION_DENIED from security rules
                // Happens if: 1) room expired or 2) ex/{roomId} missing
                if (error.code === 'PERMISSION_DENIED') {
                    // Complete cleanup: the room is no longer valid
                    localStorage.removeItem('roomSession');
                    setRoomSession(null);
                    setParticipants([]);  // Clear participants list
                    setError("errors.rooms.expired");
                } else {
                    // Other errors (network, Firebase down): log but don't clean state
                    setError("Error on useRoom -> updatePosition " + error.toString());
                }
            }
        }
    }, [roomSession]);

    const saveBluetoothToRoom = useCallback(async (app: TenantApp, roomId: string, macAddress: string, name: string): Promise<void> => {
        try {
            setLoading(true);
            await addBleDeviceToRoom(app, roomId, macAddress, name);
            addNotification({ message: t('success.ble.addSuccess'), type: 'success' });
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [addNotification, t]);

    const removeUserFromRoom = useCallback(async (app: TenantApp, roomId: string): Promise<void> => {
        try {
            setLoading(true);
            await removeUser(app, roomId);
            // Total cleanup: the user has left the room
            setRoomSession(null);
            setParticipants([]);
            setExpiredAt(0);
            localStorage.removeItem('roomSession');
            addNotification({ message: t('success.rooms.leaveSuccess'), type: 'success' });
        } catch (error: any) {
            setError(error.message);
            localStorage.removeItem('roomSession');
        } finally {
            setLoading(false);
        }
    }, [addNotification, t]);

    /**
     * Real-time subscription to room participants
     *
     * Monitors 3 Firebase events:
     * - onChildAdded: new participant joins
     * - onChildChanged: participant updates position
     * - onChildRemoved: participant exits
     *
     * IMPORTANT: Filters the current user (auth.uid) to avoid duplicates
     * (the user's position is managed locally, no subscription is needed)
     */
    useEffect(() => {
        if (!roomSession || !app || !userFirebase) return;

        // Subscribe to loc/{roomId}: receives delta updates (not full snapshot)
        const unsubscribe = subscribeToRoom(app, userFirebase.uid, roomSession.roomId, (newParticipants) => {
            // newParticipants: array of all other participants (excludes current user)
            setParticipants(newParticipants);
            //setParticipants(MOCK_PARTICIPANTS);
        });

        // Cleanup: removes listener when component unmounts or dependencies change
        return unsubscribe;
    }, [app, userFirebase, roomSession]);

    useEffect(() => {
        if (error) {
            addNotification({ message: t(error), type: 'error' });
            // After showing the error, we reset it to avoid duplicate notifications on re-render
            setError(null);
        }
    }, [error, t, addNotification]);

    return {
        roomSession,
        expiredAt,
        participants,
        loading,
        createNewRoom,
        joinExistingRoom,
        updatePosition,
        removeUserFromRoom,
        saveBluetoothToRoom
    };
};